import "./style.scss";

import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import { Modal } from "@mui/material";
import classNames from "classnames";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import InfoTooltip from "components/shared/InfoTooltip";
import Stepper from "components/shared/Stepper";
import { useCompanySettings } from "hooks/useCompanySettings";
import { cloneElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../assets/styles/_variables.module.scss";
import { IntegrationStatus } from "../../store/integrationsStore";
import { useAppSelector } from "../../store/store";

type TStyled = {
    mainTitle: string | undefined | React.ReactNode;
    subTitle: string | undefined;
    controlsTitle: string | undefined;
    className?: string;
};

type TProps = {
    open: boolean;
    onClose: () => void;
    children: JSX.Element;
    mainTitle?: string | React.ReactNode;
    subTitle?: string;
    controlsTitle?: string;
    Icon?: any;
    showCloseButton?: boolean;
    /**
     * Ignore backdrop clicks and Escape. The header close button stays available.
     */
    blockOutsideClose?: boolean;
    dictionaryKey?: string;
    noPadding?: boolean;
    style?: React.CSSProperties;
    /**
     * Data needed to display icon with tooltip for info or doc.
     */
    infoTooltip?: { text: string; type: "info" | "question"; link?: string };
    /**
     * Data needed to display expanded helpdesk icon.
     */
    helpdeskInfo?: { text: string; link: string };
};

const ModalHeader = styled.header<TStyled>((props) => ({
    borderBottom: !(props.mainTitle || props.subTitle || props.controlsTitle)
        ? "none"
        : `1px solid ${variables["border-grey"]}`,
}));

const ModalContainer = styled("div")`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: ${variables["border-radius"]};
    overflow: hidden;
    padding: 0;
    box-shadow:
        0 1px 2px rgba(20, 21, 22, 0.06),
        0 12px 32px -8px rgba(20, 21, 22, 0.18);
    transition:
        opacity 180ms cubic-bezier(0.2, 0, 0, 1),
        transform 180ms cubic-bezier(0.2, 0, 0, 1);

    @starting-style {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.96);
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;

        @starting-style {
            opacity: 1;
            transform: translate(-50%, -50%);
        }
    }
`;
const StyledModalInfo = styled.div<TStyled>((props) => ({
    display: "flex",
    gap: "16px",
    alignItems: "center",
    width: !(props.mainTitle || props.subTitle || props.controlsTitle) ? "100%" : "auto",
    justifyContent: !(props.mainTitle || props.subTitle || props.controlsTitle) ? "center" : "flex-start",
}));

const ModalBox = ({
    open,
    onClose,
    mainTitle,
    subTitle,
    controlsTitle,
    children,
    Icon,
    showCloseButton = true,
    blockOutsideClose = false,
    dictionaryKey,
    noPadding = false,
    style = {},
    infoTooltip,
    helpdeskInfo,
}: TProps) => {
    const [t] = useTranslation();
    const { hasBexioIntegration } = useAppSelector((state) => state.integrations);
    const [currentTab, setCurrentTab] = useState(0);
    const { getCompanySettings } = useCompanySettings();
    const usesMultiPeriods = getCompanySettings("uses_simultaneous_period");

    const isTermImport = ["terms", "termWithCompensationGroups"].includes(dictionaryKey ?? "");

    let steps: string[] = [];

    if (dictionaryKey === "provision") {
        steps = [
            t("ImportData.ChooseProvisionTargetStep"),
            t("ImportData.InstructionsStep"),
            t("ImportData.ImportFileStep"),
            t("ImportData.ColumnMappingStep"),
            t("ImportData.ImportingStep"),
        ];
    } else {
        steps =
            dictionaryKey === "sales" || dictionaryKey === "stock"
                ? [
                      dictionaryKey === "sales"
                          ? t("ImportData.ChooseChannelStep")
                          : t("ImportData.ChooseLocationStep"),
                      t("ImportData.InstructionsStep"),
                      t("ImportData.ImportFileStep"),
                      t("ImportData.ColumnMappingStep"),
                      t("ImportData.ImportingStep"),
                  ]
                : [
                      t("ImportData.InstructionsStep"),
                      ...(usesMultiPeriods && isTermImport ? [t("ConnectTitle.PeriodType")] : []),
                      t("ImportData.ImportFileStep"),
                      t("ImportData.ColumnMappingStep"),
                      t("ImportData.ImportingStep"),
                  ];
    }

    if (dictionaryKey === "sales" && hasBexioIntegration) {
        steps.push(t("Invoice.Invoice"));
    }

    const moveToStep = useCallback(
        (forward = false) =>
            () => {
                if (forward) {
                    setCurrentTab((value) => ++value);
                } else {
                    setCurrentTab((prev) => --prev);
                }
            },
        [],
    );

    useEffect(() => {
        if (!open) {
            setCurrentTab(0);
        }
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={(_event, reason) => {
                if (blockOutsideClose && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
                onClose();
                setCurrentTab(0);
            }}
            disableAutoFocus
        >
            <ModalContainer>
                <div
                    className={classNames({
                        "modal-wrapper": true,
                        "modal-wrapper management": dictionaryKey === "sales",
                        "modal-wrapper stock": dictionaryKey === "stock",
                    })}
                    style={style}
                >
                    <ModalHeader
                        mainTitle={mainTitle}
                        subTitle={subTitle}
                        controlsTitle={controlsTitle}
                        className="modal-header"
                    >
                        <StyledModalInfo
                            mainTitle={mainTitle}
                            subTitle={subTitle}
                            controlsTitle={controlsTitle}
                            className="modal-header-titles"
                        >
                            {Icon && <Icon />}
                            <div className="modal-header-info-titles">
                                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                    <h2 className="main-title">{mainTitle}</h2>
                                    {infoTooltip && (
                                        <InfoTooltip
                                            text={infoTooltip?.text}
                                            iconType={infoTooltip?.type}
                                            link={infoTooltip?.link}
                                        />
                                    )}
                                    {helpdeskInfo && (
                                        <ExpandedHelpdeskIcon link={helpdeskInfo?.link} text={helpdeskInfo?.text} />
                                    )}
                                </div>
                                {subTitle && <h4 className="sub-title">{subTitle}</h4>}
                            </div>
                        </StyledModalInfo>
                        {dictionaryKey && (
                            <header className="import-data-header">
                                <Stepper currentStep={currentTab} steps={steps} />
                            </header>
                        )}
                        <div className="modal-header-controls">
                            {controlsTitle && <h4 className="title">{controlsTitle}</h4>}
                            {showCloseButton && (
                                <CloseIcon
                                    className="close-icon"
                                    onClick={() => {
                                        onClose();
                                        setCurrentTab(0);
                                    }}
                                    sx={{
                                        transition: "0.4s",
                                        cursor: "pointer",
                                        color: "#B4B4B4",
                                        "&:hover": {
                                            color: "#626262",
                                        },
                                    }}
                                />
                            )}
                        </div>
                    </ModalHeader>
                    <main className="modal-main" style={noPadding ? { padding: 0 } : {}}>
                        {dictionaryKey
                            ? cloneElement(children, {
                                  moveToStep,
                                  currentTab,
                                  setCurrentTab,
                              })
                            : children}
                    </main>
                </div>
            </ModalContainer>
        </Modal>
    );
};

export default ModalBox;
